import { Validate3DSRequest } from "@kushki/js/lib/types/validate_3ds_request";
import { OtpCodeValidEnum } from "root/shared/OtpCodeValidEnum";

declare global {
  // tslint:disable-next-line:interface-name
  interface Window {
    // tslint:disable-next-line:no-any
    Cardinal: any;
  }
}

/**
 * Card Action Creators
 */
import { Kushki } from "@kushki/js";
import { BinInfoResponse } from "@kushki/js/lib/types/bin_info_response";
import { ErrorResponse } from "@kushki/js/lib/types/error_response";
import { GetBrandsLogosByMerchantResponse } from "@kushki/js/lib/types/get_brands_logos_by_merchant_response";
import { CommissionConfigurationAmount } from "@kushki/js/lib/types/remote/commission_configuration_amount";
import { CommissionConfigurationRequest } from "@kushki/js/lib/types/remote/commission_configuration_request";
import { TokenResponse } from "@kushki/js/lib/types/remote/token_response";
import { SecureOtpRequest } from "@kushki/js/lib/types/secure_otp_request";
import { SecureOtpResponse } from "@kushki/js/lib/types/secure_otp_response";
import { SubscriptionTokenRequest } from "@kushki/js/lib/types/subscription_token_request";
import { TokenRequest } from "@kushki/js/lib/types/token_request";
import { Validate3DsResponse } from "@kushki/js/lib/types/validate_3ds_response";
import { setErrorMessage } from "components/Error/store/Actions";
import {
  hideOverlay,
  showOverlay,
} from "components/OverLay/store/ActionCreators";
import { OtpTypeEnum } from "components/WaitingView/OtpTypeEnum";
import { ActionTypes } from "containers/Card/store/ActionTypes";
import { CardAction } from "containers/Card/store/CardAction";
import { CardState, ICommissionConfig } from "containers/Card/store/CardState";
import { IconBrandEnum } from "containers/Card/store/IconBrandEnum";
import { ImgBrandEnum } from "containers/Card/store/ImgBrandEnum";
import { get, isNil } from "lodash";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import {
  MESSAGE_3DS_ES_ENUM,
  VALIDATE_3DS_ENUM,
} from "root/shared/Languages/Spanish";
import { OtpCodeInvalidEnum } from "root/shared/OtpCodeInvalidEnum";
import {
  CajitaStore,
  IDeferredOptionsFetch,
  IInputProps,
  ISelectProps,
} from "root/store/CajitaStore";
import { updateDisableButton } from "root/store/Reducer";
import { IDeferredResponse } from "types/remote/deferred_response";

const BRANDS_LOGOS_DEFAULT: GetBrandsLogosByMerchantResponse[] = [
  {
    brand: IconBrandEnum.visa,
    url: ImgBrandEnum.visa,
  },
  {
    brand: IconBrandEnum.masterCard,
    url: ImgBrandEnum.masterCard,
  },
];

function getStatusValidity(state: CardState & { currency: string }): boolean {
  let isValid: boolean = true;
  const inputs: string[] = [
    "cardHolder",
    "cardNumber",
    "expDate",
    "cardCVC",
    "email",
  ];

  for (const inputName of inputs) isValid = state[inputName].valid && isValid;

  return isValid;
}

const enableTokenWithoutCvv: () => CardAction = (): CardAction => ({
  type: ActionTypes.ENABLE_TOKEN_WITHOUT_CVV,
});

const switchIsDeferred: () => CardAction = (): CardAction => ({
  type: ActionTypes.SWITCH_IS_DEFERRED,
});

const deferredOptionsValidityAction: () => CardAction = (): CardAction => ({
  type: ActionTypes.SWITCH_DEFERRED_OPTIONS_VALIDITY,
});
const resetDeferredOptions: () => CardAction = (): CardAction => ({
  type: ActionTypes.RESET_DEFERRED_OPTIONS,
});

const setDeferredOptionsAction: (
  payload: IDeferredOptionsFetch
) => CardAction = (payload: IDeferredOptionsFetch): CardAction => ({
  type: ActionTypes.SET_DEFERRED_OPTIONS,
  optionsFetched: payload,
});

const changeDeferredTypeAction: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_DEFERRED_TYPE_SELECT,
  stringPayload: payload,
});

const updateDeferredSelectValue: (payload: ISelectProps) => CardAction = (
  payload: ISelectProps
): CardAction => ({
  type: ActionTypes.UPDATE_DEFERRED_SELECT_VALUE,
  select: payload,
});

const inputAction: (payload: IInputProps) => CardAction = (
  payload: IInputProps
): CardAction => ({
  type: ActionTypes.UPDATE_CARD_INPUT,
  input: payload,
});

const setDirty: (payload: IInputProps) => CardAction = (
  payload: IInputProps
): CardAction => ({
  type: ActionTypes.SET_CARD_INPUT_DIRTY,
  input: payload,
});

const changeStatus: () => CardAction = (): CardAction => ({
  type: ActionTypes.SWITCH_CARD_VALIDITY,
});

function updateInput(
  payload: IInputProps
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (
    dispatch: ThunkDispatch<CajitaStore, undefined, CardAction>,
    getState: () => CajitaStore
  ): void => {
    dispatch(inputAction(payload));
    const state: CardState & { currency: string } = {
      ...getState().card,
      currency: getState().config.currency,
    };
    const status: boolean = getStatusValidity(state);

    if (state.isValid !== status) dispatch(changeStatus());
  };
}

const setToken: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.SET_CARD_TOKEN,
  stringPayload: payload,
});

const setCommissionConfig: (payload: ICommissionConfig) => CardAction = (
  payload: ICommissionConfig
): CardAction => ({
  type: ActionTypes.SET_COMMISSION_CONFIG,
  commissionConfiguration: payload,
});

const setCommissionFlag: (payload: boolean) => CardAction = (
  payload: boolean
): CardAction => ({
  type: ActionTypes.SET_COMMISSION_FLAG,
  hasCommission: payload,
});

const setShowCommissionsDetail: (payload: boolean) => CardAction = (
  payload: boolean
): CardAction => ({
  type: ActionTypes.SET_SHOW_COMMISSION_DETAILS,
  showCommissionsDetail: payload,
});

const setCardBrand: (payload: BinInfoResponse) => CardAction = (
  payload: BinInfoResponse
): CardAction => ({
  type: ActionTypes.UPDATE_CARD_BRAND,
  cardType: payload,
});

const setBrandsListLogosMerchant: (
  payload: GetBrandsLogosByMerchantResponse[]
) => CardAction = (
  payload: GetBrandsLogosByMerchantResponse[]
): CardAction => ({
  type: ActionTypes.UPDATE_BRANDS_LOGOS_LIST,
  listBrandsLogosPayload: payload,
});

const setSecureServiceId: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_SECURE_SERVICE_ID,
  stringPayload: payload,
});

const setSecureServiceName: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_SECURE_SERVICE_NAME,
  stringPayload: payload,
});

const updateOtpState: (payload: SecureOtpResponse) => CardAction = (
  payload: SecureOtpResponse
): CardAction => ({
  type: ActionTypes.UPDATE_OTP_STATE,
  otpStatePayload: payload,
});

const updateFailOtpMessage: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_OTP_FAIL_MESSAGE,
  stringPayload: payload,
});

const resetCardStore: () => CardAction = (): CardAction => ({
  type: ActionTypes.RESET_CARD_STATE,
});

const deleteFailOtpMessage: () => CardAction = (): CardAction => ({
  type: ActionTypes.DELETE_OTP_FAIL_MESSAGE,
});

const enableOtpView: (secureType: string | undefined) => CardAction = (
  secureType: string | undefined
): CardAction => ({
  type: ActionTypes.ENABLE_OTP_WAIT_VIEW,
  stringPayload: secureType,
});

const enableVoucherView: () => CardAction = (): CardAction => ({
  type: ActionTypes.ENABLE_VOUCHER_VIEW,
});

const hideVoucherView: () => CardAction = (): CardAction => ({
  type: ActionTypes.DISABLE_VOUCHER_VIEW,
});

const updateAmountPerMonth: (amount: string) => CardAction = (
  amount: string
): CardAction => ({
  type: ActionTypes.UPDATE_AMOUNT_PER_MONTH,
  stringPayload: amount,
});

const hideOtpView: () => CardAction = (): CardAction => ({
  type: ActionTypes.DISABLE_OTP_WAIT_VIEW,
});

const showOptError: () => CardAction = (): CardAction => ({
  type: ActionTypes.ENABLE_OTP_ERROR,
});

const hideOptError: () => CardAction = (): CardAction => ({
  type: ActionTypes.DISABLE_OTP_ERROR,
});

const updateNestedMessage: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_NESTED_MESSAGE,
  stringPayload: payload,
});

const updateNestedErrorCode: (payload: string) => CardAction = (
  payload: string
): CardAction => ({
  type: ActionTypes.UPDATE_NESTED_ERROR_CODE,
  stringPayload: payload,
});

const setExceededRetries: (payload: boolean) => CardAction = (
  payload: boolean
): CardAction => ({
  type: ActionTypes.EXCEEDED_RETRIES,
  exceededRetry: payload,
});

// tslint:disable-next-line:cognitive-complexity
function _handleTokenResponse(
  dispatch: Function,
  tokenResponse: TokenResponse | ErrorResponse,
  kushkiJs: Kushki
): void {
  if ("token" in tokenResponse) dispatch(setToken(tokenResponse.token));

  if ("secureId" in tokenResponse && tokenResponse.secureId !== undefined) {
    dispatch(setSecureServiceId(tokenResponse.secureId));

    if (
      _checkHasSecure3D(tokenResponse) &&
      !isNil(get(tokenResponse, "security"))
    )
      if (get(tokenResponse, "security.authRequired")) {
        const validate3DSRequest: Validate3DSRequest = {
          secureId: tokenResponse.secureId,
          security: {
            ...tokenResponse.security,
            authRequired: get(tokenResponse, "security.authRequired"),
          },
        };

        kushkiJs.requestValidate3DS(
          validate3DSRequest,
          (response: Validate3DsResponse) => {
            if (get(response, "isValid", false)) {
              dispatch(
                updateOtpState({ isValid: get(response, "isValid", false) })
              );
              if (response.isValid === true)
                dispatch(setToken(tokenResponse.token));
            } else {
              dispatch(
                updateFailOtpMessage(
                  MESSAGE_3DS_ES_ENUM[VALIDATE_3DS_ENUM.INVALID]
                )
              );
              dispatch(
                setErrorMessage(MESSAGE_3DS_ES_ENUM[VALIDATE_3DS_ENUM.INVALID])
              );
              dispatch(
                updateNestedMessage(
                  MESSAGE_3DS_ES_ENUM[VALIDATE_3DS_ENUM.INVALID]
                )
              );
              dispatch(updateNestedErrorCode("ERR3DS"));
            }
            dispatch(updateDisableButton(false));
          }
        );
      } else {
        dispatch(updateOtpState({ isValid: true }));
        dispatch(updateDisableButton(false));
      }
    else if (tokenResponse.secureService === OtpTypeEnum.KUSHKI) {
      dispatch(enableOtpView(tokenResponse.secureId));
      dispatch(updateDisableButton(false));
    }

    if (tokenResponse.secureService !== undefined)
      dispatch(setSecureServiceName(tokenResponse.secureService));

    dispatch(hideOverlay());
  } else {
    dispatch(enableOtpView(undefined));
    dispatch(updateDisableButton(false));
  }

  if ("message" in tokenResponse) {
    dispatch(setErrorMessage(tokenResponse.message));
    dispatch(hideOverlay());
  }

  if ("settlement" in tokenResponse && tokenResponse.settlement !== undefined) {
    dispatch(enableVoucherView());
    dispatch(updateAmountPerMonth(`${tokenResponse.settlement}`));
  } else dispatch(hideVoucherView());
}

function getCommissionsConfig(
  body: CommissionConfigurationRequest
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    dispatch(showOverlay());
    kushki.getCommissionConfiguration(
      body,
      (value: CommissionConfigurationAmount | ErrorResponse) => {
        _handleCommissionResponse(dispatch, value);
      }
    );
    dispatch(hideOverlay());
  };
}

function _handleCommissionResponse(
  dispatch: Function,
  response: CommissionConfigurationAmount | ErrorResponse
): void {
  if ("message" in response) return;

  dispatch(
    setCommissionConfig({
      commissionMerchantName: response.commissionMerchantName,
      parentMerchantName: response.parentMerchantName,
      totalAmount: response.totalAmount,
    })
  );
  dispatch(setCommissionFlag(true));
}

function getCardToken(
  body: TokenRequest
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    dispatch(updateDisableButton(true));
    dispatch(showOverlay());
    kushki.requestToken(body, (value: TokenResponse | ErrorResponse) => {
      _handleTokenResponse(dispatch, value, kushki);
    });
  };
}

function getListBrandsLogosMerchant(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    kushki.requestBrandsLogosByMerchant(
      (value: GetBrandsLogosByMerchantResponse[] | ErrorResponse) => {
        if (!value.hasOwnProperty("message")) {
          let listBrand: GetBrandsLogosByMerchantResponse[] = [];
          const card_list: GetBrandsLogosByMerchantResponse[] = value as GetBrandsLogosByMerchantResponse[];

          // istanbul ignore next
          if (card_list.length === 0) listBrand.push(...BRANDS_LOGOS_DEFAULT);
          else listBrand = listBrand.concat(card_list);

          dispatch(setBrandsListLogosMerchant(listBrand));
        } else {
          const response: ErrorResponse = value as ErrorResponse;

          setErrorMessage(response.message);
        }
      }
    );
  };
}
function getCardBrand(
  binNumber: string
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    kushki.requestBinInfo(
      {
        bin: binNumber,
      },
      (value: BinInfoResponse | ErrorResponse) => {
        if (value.hasOwnProperty("brand")) {
          const brand_card: BinInfoResponse = value as BinInfoResponse;

          dispatch(setCardBrand({ ...brand_card }));
        } else {
          let brandLogo: string = "";

          const brands: { brand: string; pattern: RegExp }[] = [
            { brand: "visa", pattern: /^4/ },
            {
              brand: "mastercard",
              pattern: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)/,
            },
            { brand: "amex", pattern: /^3[47]/ },
            { brand: "diners", pattern: /^3(?:0[0-5]|[68][0-9])/ },
            { brand: "discover", pattern: /^6(?:011|5[0-9]{2})/ },
          ];

          const new_brand:
            | { brand: string; pattern: RegExp }
            | undefined = brands.find(
            (elem: { brand: string; pattern: RegExp }) =>
              elem.pattern.test(binNumber)
          );

          brandLogo = get(new_brand, "brand", "");

          dispatch(
            setCardBrand({ brand: brandLogo, cardType: "credit", bank: "" })
          );
        }
      }
    );
  };
}

function getSubscriptionCardToken(
  body: SubscriptionTokenRequest
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    dispatch(updateDisableButton(true));
    dispatch(showOverlay());
    kushki.requestSubscriptionToken(
      body,
      (value: TokenResponse | ErrorResponse) => {
        _handleTokenResponse(dispatch, value, kushki);
      }
    );
  };
}

function getDeferredOptions(
  binNumber: string,
  language: string
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const isSubscription: boolean = getSate().config.is_subscription;

    if (!isSubscription) {
      const kushki: Kushki = getSate().config.kushkiJS;

      requestDefferedOptions(kushki, binNumber, language, dispatch);

      return;
    }

    dispatch(resetDeferredOptions());
  };
}

function requestDefferedOptions(
  kushki: Kushki,
  binNumber: string,
  language: string,
  dispatch: Function
): void {
  kushki.requestDeferred(
    {
      bin: binNumber,
    },
    (response: IDeferredResponse[] | ErrorResponse) => {
      if (Array.isArray(response))
        if (response.length > 0) {
          type DeferredOption = {
            type: string;
            months: [string];
            monthsOfGrace: [string];
          };
          const response_casted: DeferredOption[] = response as DeferredOption[];
          const options_fetched: IDeferredOptionsFetch = response_casted.reduce(
            (acc: IDeferredOptionsFetch, curr: DeferredOption) => ({
              ...acc,
              [curr.type]: {
                months: curr.months.map((month: string) => ({
                  value: month,
                  text: `${month} ${_translateMonthPlaceHolder(language)}`,
                })),
                monthsOfGrace: curr.monthsOfGrace.map((month: string) => ({
                  value: month,
                  text: `${month} ${_translateMonthPlaceHolder(language)}`,
                })),
              },
            }),
            {}
          );

          dispatch(setDeferredOptionsAction(options_fetched));
        } else dispatch(resetDeferredOptions());
      else dispatch(setErrorMessage(response.message));

      return;
    }
  );
}
// istanbul ignore next
function validateOtp(
  body: SecureOtpRequest
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, getSate: Function): void => {
    const kushki: Kushki = getSate().config.kushkiJS;

    dispatch(showOverlay());
    kushki.requestSecureServiceValidation(
      body,
      (secureOtpResponse: SecureOtpResponse | ErrorResponse) => {
        // istanbul ignore next
        if (
          _isKushkiOtpValid(secureOtpResponse) ||
          _is3dsValid(secureOtpResponse)
        )
          dispatch(updateOtpState({ isValid: true }));
        /* tslint:disable:early-exit */
        if ("message" in secureOtpResponse && _isOtpInvalid(secureOtpResponse))
          dispatch(updateOtpState({ isValid: false }));
        if (get(secureOtpResponse, "code", "") === OtpCodeInvalidEnum.OTP200)
          dispatch(setExceededRetries(true));
        dispatch(hideOverlay());
      }
    );
  };
}

// istanbul ignore next
function updateOtpValue(
  value: string
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, _getSate: Function): void => {
    const new_value: CardAction = {
      type: ActionTypes.UPDATE_OTP_VALUE,
      stringPayload: value,
    };

    dispatch(new_value);
  };
}

// istanbul ignore next
function updateEmailValidity(
  validity: boolean
): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, _getSate: Function): void => {
    const new_value: CardAction = {
      type: ActionTypes.UPDATE_EMAIL_VALIDITY,
      emailValid: validity,
    };

    dispatch(new_value);
  };
}

// istanbul ignore next
function hideModal(): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(hideOtpView());
  };
}
// istanbul ignore next
function showNesterErrorModal(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(showOptError());
  };
}

// istanbul ignore next
function hideNesterErrorModal(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(hideOptError());
  };
}
// istanbul ignore next
function notifyUpdate(): ThunkAction<void, CajitaStore, undefined, CardAction> {
  return (dispatch: Function, _getSate: Function): void => {
    const update_value: CardAction = {
      type: ActionTypes.UPDATE_STATE,
    };

    dispatch(update_value);
  };
}

// istanbul ignore next
function deleteErrorMessage(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(deleteFailOtpMessage());
  };
}

function resetCardState(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(resetCardStore());
    dispatch(hideOverlay());
  };
}

function disableVoucherView(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    const disable_voucher: CardAction = {
      type: ActionTypes.DISABLE_VOUCHER_VIEW,
    };

    dispatch(disable_voucher);
  };
}

function enableCvvView(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  CardAction
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(enableTokenWithoutCvv());
  };
}

function _translateMonthPlaceHolder(language: string): string {
  const placeholder_month: Record<string, string> = {
    [`es`]: "meses",
    [`en`]: "months",
    [`br`]: "meses",
  };

  return placeholder_month[language];
}

function _checkHasSecure3D(tokenInfo: TokenResponse): boolean {
  return tokenInfo.secureService === OtpTypeEnum["3DSECURE"];
}

function _isKushkiOtpValid(
  secureOtpResponse: SecureOtpResponse | ErrorResponse
): boolean {
  return (
    "message" in secureOtpResponse &&
    secureOtpResponse.message === "ok" &&
    secureOtpResponse.code === OtpCodeValidEnum.OTP000
  );
}

// tslint:disable-next-line:no-identical-functions
function _is3dsValid(
  secureOtpResponse: SecureOtpResponse | ErrorResponse
): boolean {
  return (
    "message" in secureOtpResponse &&
    secureOtpResponse.message === OtpCodeValidEnum["3DS000"] &&
    secureOtpResponse.code === "ok"
  );
}

function _isOtpInvalid(
  secureOtpResponse: SecureOtpResponse | ErrorResponse
): boolean {
  return (
    "message" in secureOtpResponse &&
    secureOtpResponse.message !== "ok" &&
    secureOtpResponse.message !== OtpCodeValidEnum["3DS000"]
  );
}

export {
  setSecureServiceName,
  enableCvvView,
  setDirty,
  updateInput,
  getCardToken,
  deferredOptionsValidityAction,
  getSubscriptionCardToken,
  getCommissionsConfig,
  setCommissionConfig,
  setCommissionFlag,
  setShowCommissionsDetail,
  setBrandsListLogosMerchant,
  getDeferredOptions,
  changeDeferredTypeAction,
  updateDeferredSelectValue,
  switchIsDeferred,
  inputAction,
  changeStatus,
  setToken,
  setCardBrand,
  getListBrandsLogosMerchant,
  getCardBrand,
  validateOtp,
  updateOtpValue,
  hideModal,
  showNesterErrorModal,
  hideNesterErrorModal,
  notifyUpdate,
  deleteErrorMessage,
  resetCardState,
  disableVoucherView,
  updateEmailValidity,
};



// WEBPACK FOOTER //
// ./src/containers/Card/store/Actions.tsx